import { useCallback, useEffect, useState } from 'react';

// Browsers fire `beforeinstallprompt` once the app meets installability
// criteria. We stash the event so a user-triggered action (Install button
// in the sidebar) can call .prompt() — never on first load.
//
// `appinstalled` and the display-mode media query both fire when the user
// completes install or launches the installed PWA; both cases hide the button.

const STANDALONE_QUERY = '(display-mode: standalone)';

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia && window.matchMedia(STANDALONE_QUERY).matches) return true;
  return Boolean(window.navigator && window.navigator.standalone);
};

export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const onBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };
    const onInstalled = () => {
      setDeferredPrompt(null);
      setInstalled(true);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);

    let mql;
    if (window.matchMedia) {
      mql = window.matchMedia(STANDALONE_QUERY);
      const onChange = (e) => {
        if (e.matches) onInstalled();
      };
      if (mql.addEventListener) mql.addEventListener('change', onChange);
      else if (mql.addListener) mql.addListener(onChange);
      return () => {
        window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
        window.removeEventListener('appinstalled', onInstalled);
        if (mql.removeEventListener) mql.removeEventListener('change', onChange);
        else if (mql.removeListener) mql.removeListener(onChange);
      };
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return 'unavailable';
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    return choice && choice.outcome ? choice.outcome : 'dismissed';
  }, [deferredPrompt]);

  return {
    canInstall: Boolean(deferredPrompt) && !installed,
    installed,
    promptInstall
  };
}
